import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import Logo from './Logo';
import { HomeIcon, Bars3Icon, XMarkIcon, WalletIcon } from '@heroicons/react/24/outline';

function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { currentUser, logout, loading, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Add scroll detection for navbar appearance change
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  // Define navigation items based on user role and current page
  const getNavigationItems = () => {
    const baseNavigation = [
      { name: 'Home', href: '/' },
      { name: 'About', href: '/about' },
      { name: 'Investment Plans', href: '/investment-plans' },
      { name: 'Portfolio', href: '/portfolio' },
      { name: 'Contact', href: '/contact' }
    ];

    // Add dashboard link if user is logged in and not an admin
    if (currentUser && !isAdmin && location.pathname !== '/dashboard') {
      baseNavigation.push({
        name: 'Dashboard',
        href: '/dashboard',
        icon: WalletIcon,
        className: 'text-white bg-primary-600 hover:bg-primary-700 rounded-md px-4 py-2'
      });
    }

    return baseNavigation;
  };

  const navigation = getNavigationItems();

  const handleLogout = async (e) => {
    e.preventDefault();
    try {
      setIsOpen(false); // Close mobile menu
      await logout();
      // Use replace to prevent going back to protected routes
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error logging out:', error);
      // Continue with navigation even if logout fails
      navigate('/login', { replace: true });
    }
  };

  const menuVariants = {
    closed: {
      opacity: 0,
      height: 0,
      transition: {
        duration: 0.3,
        ease: 'easeInOut'
      }
    },
    open: {
      opacity: 1,
      height: 'auto',
      transition: {
        duration: 0.3,
        ease: 'easeInOut',
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    closed: { 
      opacity: 0, 
      y: -10,
      transition: { duration: 0.2 }
    },
    open: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.3 }
    }
  };

  // Don't render navigation items while auth is loading
  if (loading) {
    return (
      <>
        <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-20">
              <Link to="/" className="flex-shrink-0">
                <Logo />
              </Link>
            </div>
          </div>
        </nav>
        <div className="h-20" />
      </>
    );
  }

  return (
    <>
      <motion.nav 
        className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'}`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex-shrink-0">
              <Logo />
            </Link>

            {/* Desktop Menu */}
            <div className="hidden md:flex md:items-center md:space-x-1 lg:space-x-8">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`relative px-3 py-2 text-sm font-medium transition-colors duration-200
                      ${item.className || (isActive 
                        ? 'text-primary-600' 
                        : 'text-neutral-600 hover:text-primary-500')}`}
                  >
                    {item.icon && <item.icon className="w-5 h-5 mr-2 inline-block" />}
                    {item.name}
                    {isActive && !item.className && (
                      <motion.div 
                        className="absolute bottom-0 left-0 w-full h-0.5 bg-primary-600"
                        layoutId="navbar-indicator"
                      />
                    )}
                  </Link>
                );
              })}
              
              <div className="hidden md:flex ml-6 items-center space-x-3">
                {currentUser ? (
                  <>
                    {isAdmin && location.pathname === '/admin' ? (
                      <motion.button
                        onClick={handleLogout}
                        className="inline-flex items-center justify-center px-5 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 shadow-sm hover:shadow transition-all duration-200"
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                      >
                        Log Out
                      </motion.button>
                    ) : isAdmin ? (
                      <Link
                        to="/admin"
                        className="inline-flex items-center justify-center px-5 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 shadow-sm hover:shadow transition-all duration-200"
                      >
                        Admin Dashboard
                      </Link>
                    ) : (
                      <motion.button
                        onClick={handleLogout}
                        className="inline-flex items-center justify-center px-5 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 shadow-sm hover:shadow transition-all duration-200"
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                      >
                        Log Out
                      </motion.button>
                    )}
                  </>
                ) : (
                  <>
                    <motion.div
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      <Link
                        to="/login"
                        className="inline-flex items-center justify-center px-5 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 shadow-sm hover:shadow transition-all duration-200"
                      >
                        Login
                      </Link>
                    </motion.div>
                    <motion.div
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      <Link
                        to="/signup"
                        className="inline-flex items-center justify-center px-5 py-2 border border-primary-600 text-sm font-medium rounded-md text-primary-600 hover:text-primary-700 hover:border-primary-700 hover:bg-primary-50 transition-colors duration-200"
                      >
                        Create Account
                      </Link>
                    </motion.div>
                  </>
                )}
              </div>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center space-x-3">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-neutral-600 hover:text-primary-600 hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 transition-colors duration-200"
              >
                <span className="sr-only">Open main menu</span>
                {!isOpen ? (
                  <Bars3Icon className="block h-6 w-6" />
                ) : (
                  <XMarkIcon className="block h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial="closed"
              animate="open"
              exit="closed"
              variants={menuVariants}
              className="md:hidden bg-white border-t border-neutral-100 shadow-lg overflow-hidden"
            >
              <div className="px-3 pt-3 pb-4 space-y-1">
                {navigation.map((item, index) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <motion.div
                      key={item.href}
                      variants={itemVariants}
                      custom={index}
                    >
                      <Link
                        to={item.href}
                        className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors duration-200
                          ${isActive 
                            ? 'text-primary-600 bg-primary-50' 
                            : 'text-neutral-600 hover:text-primary-600 hover:bg-neutral-50'}`}
                        onClick={() => setIsOpen(false)}
                      >
                        {item.name}
                      </Link>
                    </motion.div>
                  );
                })}
                
                <div className="mt-6 px-3 space-y-3">
                  {currentUser ? (
                    <>
                      {isAdmin && location.pathname === '/admin' ? (
                        <motion.button
                          onClick={handleLogout}
                          className="block w-full px-4 py-3 text-center text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 shadow-sm transition-colors duration-200"
                          variants={itemVariants}
                        >
                          Log Out
                        </motion.button>
                      ) : isAdmin ? (
                        <motion.div variants={itemVariants}>
                          <Link
                            to="/admin"
                            className="block w-full px-4 py-3 text-center text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 shadow-sm transition-colors duration-200"
                            onClick={() => setIsOpen(false)}
                          >
                            Admin Dashboard
                          </Link>
                        </motion.div>
                      ) : (
                        <motion.button
                          onClick={handleLogout}
                          className="block w-full px-4 py-3 text-center text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 shadow-sm transition-colors duration-200"
                          variants={itemVariants}
                        >
                          Log Out
                        </motion.button>
                      )}
                    </>
                  ) : (
                    <>
                      <motion.div variants={itemVariants}>
                        <Link
                          to="/login"
                          className="block w-full px-4 py-3 text-center text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 shadow-sm transition-colors duration-200"
                          onClick={() => setIsOpen(false)}
                        >
                          Login
                        </Link>
                      </motion.div>
                      <motion.div variants={itemVariants}>
                        <Link
                          to="/signup"
                          className="block w-full px-4 py-3 text-center text-sm font-medium rounded-lg text-primary-600 border border-primary-600 hover:bg-primary-50 hover:border-primary-700 transition-colors duration-200"
                          onClick={() => setIsOpen(false)}
                        >
                          Create Account
                        </Link>
                      </motion.div>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>
      {/* Spacer to prevent content from hiding under fixed navbar */}
      <div className="h-20" />
    </>
  );
}

export default Navigation;